import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import useTheme from "../ColorConfig/usetheme";
import SEO from "../../AASeo/Seo";

const organization = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "IDLBAY Private Limited",
  alternateName: "IDLBAY",
  url: "https://idlbay.com",
  logo: "https://idlbay.com/idlbaylogo.png",
  sameAs: [
    "https://www.linkedin.com/company/idlbay/",
    "https://x.com/idlbayPvtLtd",
    "https://www.instagram.com/idlbay.official?igsh=dnIxMm50cmdxNDNy",
    "https://www.facebook.com/profile.php?id=61582746578911",
    "https://www.youtube.com/channel/UCDSb9__-mSLqeyuZYelakcw",
  ],
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+91-9840841840",
    contactType: "Customer Support",
    areaServed: "IN",
    availableLanguage: ["English", "Tamil"],
  },
  address: {
    "@type": "PostalAddress",
    streetAddress:
      "Office No.1, First Floor, Door No.1, B Narasimhan 3rd Street",
    addressLocality: "T-Nagar, Chennai",
    addressRegion: "Tamil Nadu",
    postalCode: "600017",
    addressCountry: "IN",
  },
};

const TechBlogPage = () => {
  const colors = useTheme();
  const navigate = useNavigate();
  const [blogPosts, setBlogPosts] = useState([]);
  const [filteredPosts, setFilteredPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [visibleCards, setVisibleCards] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const cardRefs = useRef([]);
  const observerRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    // Check if device is mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    fetchBlogPosts();

    return () => {
      window.removeEventListener("resize", checkMobile);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  // Filter posts when search query changes
  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredPosts(blogPosts);
    } else {
      const query = searchQuery.toLowerCase().trim();
      const filtered = blogPosts.filter(
        (post) =>
          post.title.toLowerCase().includes(query) ||
          post.excerpt.toLowerCase().includes(query) ||
          post.category.toLowerCase().includes(query) ||
          post.fullContent?.toLowerCase().includes(query)
      );
      setFilteredPosts(filtered);
    }
  }, [searchQuery, blogPosts]);

  // Set up Intersection Observer to detect visible cards
  useEffect(() => {
    if (filteredPosts.length === 0) return;

    const options = {
      root: null,
      rootMargin: "0px",
      threshold: 0.2, // Trigger when 20% of the card is visible
    };

    const handleIntersect = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const index = parseInt(entry.target.dataset.index, 10);
          setVisibleCards((prev) => new Set(prev).add(index));
        }
      });
    };

    observerRef.current = new IntersectionObserver(handleIntersect, options);

    // Observe all cards
    cardRefs.current.forEach((card) => {
      if (card) observerRef.current.observe(card);
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [filteredPosts]);

  const fetchBlogPosts = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        "https://api.dremerz.net/api/DZ_8923_Idlbay_Blog_BlogList"
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Transform the API data to match our expected format
      const transformedPosts = data.reverse().map((post) => ({
        id: post.id,
        category: post.category,
        date: post.date,
        title: post.title,
        excerpt: post.excerpt,
        image: `https://api.dremerz.net/uploads/${post.image}`,
        fullContent: post.fullContent,
        // Include secondary images
        secondaryImage1: post.secondaryImage1 || "",
        secondaryImage2: post.secondaryImage2 || "",
        secondaryImage3: post.secondaryImage3 || "",
        // If API uses different field name, include that too
        secondaryImages: post.secondaryImages || [],
      }));

      setBlogPosts(transformedPosts);
      setFilteredPosts(transformedPosts);
      setError(null);
    } catch (err) {
      console.error("Error fetching blog posts:", err);
      setError("Failed to load blog posts. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = (post) => {
    navigate("/blogdetail", { state: { post } });
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const clearSearch = () => {
    setSearchQuery("");
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  // Animation variants for cards
  const cardVariants = {
    hidden: (index) => ({
      opacity: 0,
      x: index % 2 === 0 ? -100 : 100, // Even index from left, odd from right
      y: 20,
      scale: 0.95,
    }),
    visible: {
      opacity: 1,
      x: 0,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  // Animation for header
  const headerVariants = {
    hidden: { opacity: 0, y: -30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        ease: "easeOut",
      },
    },
  };

  // Animation for search bar
  const searchVariants = {
    hidden: { opacity: 0, y: -20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
        delay: 0.2,
      },
    },
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          backgroundColor: colors.bodyBlack,
          fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div style={{ color: colors.white, fontSize: "1.2rem" }}>
          Loading blog posts...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          minHeight: "100vh",
          backgroundColor: colors.bodyBlack,
          fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          gap: "20px",
        }}
      >
        <div style={{ color: colors.white, fontSize: "1.2rem" }}>{error}</div>
        <button
          onClick={fetchBlogPosts}
          style={{
            backgroundColor: `var(--color-primary)`,
            color: colors.white,
            padding: "10px 20px",
            border: "none",
            borderRadius: "8px",
            fontSize: "1rem",
            fontWeight: "600",
            cursor: "pointer",
            transition: "all 0.3s ease",
          }}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="Tech Blog | IDLBAY - Insights & Updates"
        description="Read the latest tech insights, news, and updates from IDLBAY. Stay informed about IT trends, software, and digital innovation."
        keywords="IDLBAY blog, tech blog, IT news, software updates, digital trends, technology insights"
        url="https://idlbay.com/blog"
        organization={organization}
      />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
        style={{
          minHeight: "100vh",
          backgroundColor: colors.bodyBlack,
          fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
        }}
      >
        {/* Header Section */}
        <motion.div
          className="blogsection"
          initial="hidden"
          animate="visible"
          variants={headerVariants}
          style={{
            padding: isMobile ? "20px 0 20px 0" : "40px 0 30px 0",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              maxWidth: "1200px",
              margin: "0 auto",
              padding: isMobile ? "0 15px" : "0 20px",
              position: "relative",
              zIndex: 1,
            }}
          >
            <div style={{ textAlign: "center" }}>
              <h2
                className="main-header"
                style={{
                  color: colors.white,
                  fontSize: isMobile ? "1.8rem" : "2.5rem",
                  fontWeight: "800",
                  margin: "0 0 5px 0",
                }}
              >
                Tech Insights & Updates
              </h2>

              {/* Search Bar */}
              <motion.div
                variants={searchVariants}
                initial="hidden"
                animate="visible"
                style={{
                  maxWidth: "600px",
                  margin: "30px auto 0 auto",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search blog posts by title, content, or category..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    style={{
                      width: "100%",
                      padding: isMobile
                        ? "8px 15px 10px 40px"
                        : "15px 55px 15px 50px",
                      backgroundColor: "rgba(255, 255, 255, 0.08)",
                      border: `1px solid rgba(255, 255, 255, 0.15)`,
                      borderRadius: "50px",
                      color: colors.white,
                      fontSize: isMobile ? "0.9rem" : "1rem",
                      fontWeight: "400",
                      outline: "none",
                      transition: "all 0.3s ease",
                    }}
                    onFocus={(e) => {
                      e.target.style.backgroundColor =
                        "rgba(255, 255, 255, 0.12)";
                      e.target.style.borderColor = `var(--color-primary)`;
                    }}
                    onBlur={(e) => {
                      e.target.style.backgroundColor =
                        "rgba(255, 255, 255, 0.08)";
                      e.target.style.borderColor = "rgba(255, 255, 255, 0.15)";
                    }}
                    className="search-input" // Add this class
                  />
                  {/* Search Icon */}
                  <div
                    style={{
                      position: "absolute",
                      left: isMobile ? "15px" : "20px",
                      color: "rgba(255, 255, 255, 0.6)",
                    }}
                  >
                    <svg
                      width={isMobile ? "15" : "20"}
                      height={isMobile ? "15" : "20"}
                      fill="currentColor"
                      viewBox="0 0 16 16"
                    >
                      <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
                    </svg>
                  </div>

                  {/* Clear Button (visible when there's text) */}
                  {searchQuery && (
                    <button
                      onClick={clearSearch}
                      style={{
                        position: "absolute",
                        right: isMobile ? "15px" : "20px",
                        background: "none",
                        border: "none",
                        color: "rgba(255, 255, 255, 0.6)",
                        cursor: "pointer",
                        padding: "5px",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.color = "rgba(255, 255, 255, 0.9)";
                        e.target.style.backgroundColor =
                          "rgba(255, 255, 255, 0.1)";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.color = "rgba(255, 255, 255, 0.6)";
                        e.target.style.backgroundColor = "transparent";
                      }}
                    >
                      <svg
                        width="16"
                        height="16"
                        fill="currentColor"
                        viewBox="0 0 16 16"
                      >
                        <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
                      </svg>
                    </button>
                  )}
                </div>

                {/* Search Results Count */}
                {searchQuery && (
                  <div
                    style={{
                      textAlign: "center",
                      marginTop: "10px",
                      color: "rgba(255, 255, 255, 0.7)",
                      fontSize: "0.9rem",
                    }}
                  >
                    Found {filteredPosts.length} result
                    {filteredPosts.length !== 1 ? "s" : ""} for "{searchQuery}"
                  </div>
                )}
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Blog Posts */}
        <div
          style={{
            maxWidth: "1200px",
            margin: "0 auto",
            padding: isMobile ? "5px 15px" : "5px 20px",
            paddingBottom: "30px",
          }}
        >
          {filteredPosts.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                color: colors.white,
                padding: "60px 0",
              }}
            >
              {searchQuery ? (
                <>
                  <div
                    style={{
                      fontSize: "1.5rem",
                      marginBottom: "10px",
                      opacity: 0.8,
                    }}
                  >
                    No results found
                  </div>
                  <div style={{ opacity: 0.6, marginBottom: "20px" }}>
                    No blog posts match your search for "{searchQuery}"
                  </div>
                  <button
                    onClick={clearSearch}
                    style={{
                      backgroundColor: `var(--color-primary)`,
                      color: colors.white,
                      padding: "10px 20px",
                      border: "none",
                      borderRadius: "8px",
                      fontSize: "1rem",
                      fontWeight: "600",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      //  width: isMobile ? '100%' : '30%',
                    }}
                  >
                    Clear Search
                  </button>
                </>
              ) : (
                "No blog posts available."
              )}
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: isMobile ? "20px" : "30px",
              }}
            >
              {filteredPosts.map((post, index) => (
                <motion.article
                  key={post.id}
                  className="blog-article"
                  custom={index}
                  initial="hidden"
                  animate={visibleCards.has(index) ? "visible" : "hidden"}
                  variants={cardVariants}
                  whileHover={{
                    y: -5,
                    transition: { duration: 0.2 },
                  }}
                  ref={(el) => {
                    cardRefs.current[index] = el;
                    if (el) el.dataset.index = index;
                  }}
                  style={{
                    backgroundColor: colors.bodyBlack,
                    borderRadius: "16px",
                    overflow: "hidden",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    position: "relative",
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    minHeight: isMobile ? "auto" : "250px",
                    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                  }}
                  onClick={() => handleCardClick(post)}
                >
                  {/* Image on the left side (top on mobile) */}
                  <div
                    className="image-container"
                    style={{
                      width: isMobile ? "100%" : "40%",
                      minWidth: isMobile ? "100%" : "40%",
                      height: isMobile ? "200px" : "250px",
                      overflow: "hidden",
                      position: "relative",
                      flexShrink: 0,
                    }}
                  >
                    <img
                      src={post.image}
                      alt={post.title}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        transition: "transform 0.3s ease",
                      }}
                    />

                    {/* Category badge in left corner */}
                    <div
                      className="category-badge"
                      style={{
                        position: "absolute",
                        top: isMobile ? "12px" : "16px",
                        left: isMobile ? "12px" : "16px",
                        backgroundColor: `var(--color-primary)`,
                        color: colors.white,
                        padding: isMobile ? "6px 12px" : "8px 16px",
                        borderRadius: "20px",
                        fontSize: isMobile ? "0.75rem" : "0.85rem",
                        fontWeight: "600",
                      }}
                    >
                      {post.category}
                    </div>

                    {/* Date in right corner */}
                    <div
                      className="date-badge"
                      style={{
                        position: "absolute",
                        top: isMobile ? "12px" : "16px",
                        right: isMobile ? "12px" : "16px",
                        backgroundColor: "rgba(0, 0, 0, 0.7)",
                        color: colors.white,
                        padding: isMobile ? "6px 10px" : "8px 12px",
                        borderRadius: "20px",
                        fontSize: isMobile ? "0.7rem" : "0.8rem",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <svg
                        width="14"
                        height="14"
                        fill="currentColor"
                        viewBox="0 0 16 16"
                      >
                        <path d="M3.5 0a.5.5 0 0 1 .5.5V1h6V.5a.5.5 0 0 1 1 0V1h1.5A1.5 1.5 0 0 1 14 2.5v11a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 2 13.5v-11A1.5 1.5 0 0 1 3.5 1H5V.5a.5.5 0 0 1 .5-.5zM3 2.5v1h10v-1a.5.5 0 0 0-.5-.5h-9a.5.5 0 0 0-.5.5zM3 4.5v9a.5.5 0 0 0 .5.5h9a.5.5 0 0 0 .5-.5v-9H3z" />
                      </svg>
                      {post.date}
                    </div>
                  </div>

                  {/* Content on the right side (below image on mobile) */}
                  <div
                    className="content-container"
                    style={{
                      padding: isMobile ? "16px" : "24px",
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                    }}
                  >
                    <div>
                      {/* Title */}
                      <h2
                        className="blog-title"
                        style={{
                          color: colors.white,
                          fontSize: isMobile ? "1.1rem" : "1.5rem",
                          fontWeight: "700",
                          margin: "0 0 16px 0",
                          lineHeight: isMobile ? "1.3" : "1.4",
                        }}
                      >
                        {post.title}
                      </h2>

                      {/* Excerpt */}
                      <div
                        className="excerpt"
                        style={{
                          color: colors.white,
                          fontSize: isMobile ? "0.9rem" : "1rem",
                          lineHeight: isMobile ? "1.5" : "1.6",
                          marginBottom: "20px",
                          opacity: "0.9",
                        }}
                      >
                        <h6>{post.excerpt}</h6>
                      </div>
                    </div>

                    {/* Read More Button */}
                    <div>
                      <button
                        className="read-more-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCardClick(post);
                        }}
                        style={{
                          backgroundColor: "transparent",
                          color: colors.white,
                          padding: isMobile ? "8px 2px" : "10px 2px",
                          border: "none",
                          borderRadius: "8px",
                          fontSize: isMobile ? "0.85rem" : "0.95rem",
                          fontWeight: "600",
                          cursor: "pointer",
                          transition: "all 0.3s ease",
                          outline: "none",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        Read More
                        <svg
                          width="16"
                          height="16"
                          fill="currentColor"
                          viewBox="0 0 16 16"
                        >
                          <path
                            fillRule="evenodd"
                            d="M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8z"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>

        {/* Responsive styles */}
        <style>
          {`
          @media (max-width: 768px) {
            /* Header adjustments for mobile */
            .main-header {
              font-size: 1.8rem !important;
              line-height: 1.2 !important;
            }

              .search-input::placeholder {
        font-size: 0.8rem !important;
      }
            
            /* Search bar mobile adjustments */
            .blogsection {
              padding: 20px 0 20px 0 !important;
            }
            
            /* Article layout changes for mobile */
            .blog-article {
              flex-direction: column !important;
              min-height: auto !important;
            }
            
            /* Image container adjustments */
            .image-container {
              width: 100% !important;
              min-width: 100% !important;
              height: 200px !important;
            }
            
            /* Badge adjustments for mobile */
            .category-badge {
              font-size: 0.75rem !important;
              padding: 6px 12px !important;
              top: 12px !important;
              left: 12px !important;
            }
            
            .date-badge {
              font-size: 0.7rem !important;
              padding: 6px 10px !important;
              top: 12px !important;
              right: 12px !important;
            }
            
            /* Content container adjustments */
            .content-container {
              padding: 16px 20px !important;
            }
            
            /* Blog title adjustments for mobile */
            .blog-title {
              font-size: 1.1rem !important;
              margin: 0 0 12px 0 !important;
              line-height: 1.3 !important;
            }
            
            /* Excerpt adjustments */
            .excerpt {
              font-size: 0.85rem !important;
              margin-bottom: 16px !important;
              line-height: 1.5 !important;
            }
            
            /* Read more button adjustments */
            .read-more-btn {
              font-size: 0.85rem !important;
              padding: 8px 2px !important;
            }
            
            /* Container padding adjustments */
            .blog-article + .blog-article {
              margin-top: 20px;
            }
          }
          
          @media (max-width: 480px) {
            /* Extra small screens */
            .blogsection{
              margin-top: -10px;
              margin-bottom: -10px;
            }
                .search-input::placeholder {
        font-size: 0.8rem !important;
      }
            
            .main-header {
              font-size: 1.5rem !important;
            }
            
            .blog-title {
              font-size: 1.1rem !important;
            }
            
            .excerpt {
              font-size: 0.9rem !important;
            }
            
            .category-badge {
              font-size: 0.7rem !important;
              padding: 5px 10px !important;
            }
            
            .date-badge {
              font-size: 0.65rem !important;
              padding: 5px 8px !important;
            }
            
            .content-container {
              padding: 10px 0px !important;
            }
            
            .image-container {
              height: 180px !important;
            }
          }
        `}
        </style>
      </motion.div>
    </>
  );
};

export default TechBlogPage;
